// Business logic: Voting store manages all election voting state with localStorage persistence.
// This store tracks which elections users have voted in to prevent duplicate voting
// and ensures users cannot return to voting forms after successful vote submission.
// Persists across browser sessions to maintain voting integrity.

import { create } from "zustand";
import { persist } from "zustand/middleware";

interface VotingStore {
  // Array of election IDs that the user has already voted for
  votedElections: number[];
  
  // Actions to manage voting state
  setVotedElections: (votedElections: number[]) => void;
  addVotedElection: (electionId: number) => void;
  hasVotedInElection: (electionId: number) => boolean;
  clearVotedElections: () => void;
}

export const useVotingStore = create<VotingStore>()(
  persist(
    (set, get) => ({
      // State: Track elections the user has already voted for
      votedElections: [],
      
      // Action: Set the entire voted elections array (useful for initial load from server)
      setVotedElections: (votedElections: number[]) => 
        set({ votedElections }),
      
      // Action: Add a single election ID to voted elections after successful vote submission
      addVotedElection: (electionId: number) => 
        set((state) => ({ 
          votedElections: [...state.votedElections, electionId] 
        })),
      
      // Selector: Check if user has voted in a specific election
      hasVotedInElection: (electionId: number) => 
        get().votedElections.includes(electionId),
      
      // Action: Clear all voted elections (useful for testing or user logout)
      clearVotedElections: () => 
        set({ votedElections: [] }),
    }),
    {
      name: "nsc-voting-storage", // localStorage key
      // No partialize needed - persist the entire voting store
    }
  )
); 